import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import type { IntegrationDraftCopy, IntegrationDraftRate } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { formatRate } from './builderModel';

export function Field({
  id,
  label,
  hint,
  optional,
  children,
  className,
}: {
  id?: string;
  label: string;
  hint?: ReactNode;
  optional?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        {optional ? <span className="text-[11px] text-muted-foreground">Optional</span> : null}
      </div>
      {children}
      {hint ? <p className="text-xs leading-5 text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function StepFrame({
  title,
  description,
  children,
  dirty,
  saving,
  readOnly,
  onSave,
  onContinue,
  continueLabel = 'Save & continue',
  aside,
}: {
  title: string;
  description: ReactNode;
  children: ReactNode;
  dirty?: boolean;
  saving?: boolean;
  readOnly?: boolean;
  onSave?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  aside?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="mb-6 max-w-3xl">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        <div className="mt-1 text-sm leading-6 text-muted-foreground">{description}</div>
      </div>
      <div className={cn('grid gap-8', aside ? 'xl:grid-cols-[minmax(0,1fr)_320px]' : '')}>
        <div className="min-w-0 space-y-6">{children}</div>
        {aside ? <div className="hidden xl:block">{aside}</div> : null}
      </div>
      {readOnly || (!onSave && !onContinue) ? null : (
        <div className="sticky bottom-0 mt-8 flex items-center justify-end gap-3 border-t border-border bg-background/95 py-4 backdrop-blur">
          <span className="mr-auto text-xs text-muted-foreground">{dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          {onSave ? (
            <Button variant="outline" onClick={onSave} disabled={saving || !dirty}>
              Save
            </Button>
          ) : null}
          {onContinue ? (
            <Button onClick={onContinue} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {continueLabel}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function IntegrationLogo({ src, color, title, size = 'md' }: { src?: string; color: string; title: string; size?: 'sm' | 'md' | 'lg' }) {
  const box = size === 'sm' ? 'h-8 w-8 text-xs' : size === 'lg' ? 'h-14 w-14 text-lg' : 'h-10 w-10 text-sm';
  return (
    <div
      className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border font-semibold', box)}
      style={{ backgroundColor: src ? '#ffffff' : color, color: '#ffffff' }}
    >
      {src ? <img src={src} alt="" className="h-full w-full object-contain p-1.5" /> : (title.trim()[0] || '?').toUpperCase()}
    </div>
  );
}

/** How the card reads in the Studio integrations catalog. */
export function IntegrationCardPreview({
  copy,
  category,
  brandColor,
  logoDataUrl,
  pricing,
}: {
  copy: IntegrationDraftCopy;
  category: string;
  brandColor: string;
  logoDataUrl?: string;
  pricing: IntegrationDraftRate[];
}) {
  const title = copy.title || 'Untitled integration';
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Catalog preview</div>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="h-1" style={{ backgroundColor: brandColor }} />
        <div className="space-y-3 p-4">
          <div className="flex items-start gap-3">
            <IntegrationLogo src={logoDataUrl} color={brandColor} title={title} />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{title}</div>
              <div className="text-xs text-muted-foreground">{category}</div>
            </div>
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            {copy.description || 'A one-line description appears here.'}
          </p>
          {copy.capabilities.length ? (
            <ul className="space-y-1">
              {copy.capabilities.slice(0, 4).map((capability) => (
                <li key={capability} className="flex gap-2 text-xs">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-brand" />
                  <span>{capability}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {pricing.length ? (
            <div className="space-y-2 border-t border-border pt-3">
              {pricing.map((rate) => (
                <div key={rate.id} className="text-[11px] leading-4">
                  <div className="text-muted-foreground">{copy.rateLabels[rate.id] || rate.id}</div>
                  <div className="font-mono text-foreground">{formatRate(rate)}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
